import CookieConsentServiceInstance from './CookieConsentService';

import { CookieCategory } from '@shared-types/optanon.types';
import { language } from '@utilities/browser';
import { getCountry } from '@utilities/country';
import { watchAddedNodes } from '@utilities/dom';
import { bannerAdsContext } from '@utilities/WindowContext';

const outOfPageTypes = ['slider', 'slider-all', 'interstitial'];

type BannerOptions = {
  adPath: string;
  countryTargeting: boolean;
  competitionBanner: string;
  width: number;
  height: number;
  hostCityTargeting: string;
  position: string;
  sponsor: string;
  context: string;
  type: string;
};

export class GptBannerService {
  sliderTimer: NodeJS.Timeout;
  gt: googletag.Googletag;

  static loadGoogleTag(): Promise<googletag.Googletag> {
    return CookieConsentServiceInstance.appendCookieScript(
      'https://www.googletagservices.com/tag/js/gpt.js',
      CookieCategory.ESSENTIAL,
      'googletag',
    );
  }

  observer = new IntersectionObserver((intersectedEls) => {
    const elements = intersectedEls.filter((el) => el.isIntersecting).map((el) => el.target as HTMLElement);
    elements.forEach((el) => {
      this.observer.unobserve(el);
      this.displayBanner(el);
    });
  });

  async init() {
    await GptBannerService.loadGoogleTag();

    googletag.cmd.push(() => {
      googletag.pubads().collapseEmptyDivs();
      googletag.pubads().setCentering(true);
      googletag.pubads().enableSingleRequest();
      googletag.pubads().disableInitialLoad();
      googletag.pubads().enableLazyLoad();
      googletag.enableServices();
    });

    watchAddedNodes('.js-gpt-banner', (el) => {
      if (el.getBoundingClientRect().width === 0 && el.getBoundingClientRect().height === 0) {
        el.setAttribute('id', `${el.id}-hidden`);
        return;
      }
      return googletag.cmd.push(() => {
        this.defineBanner(el);
      });
    });
  }

  detach() {
    this.observer && this.observer.disconnect();
  }

  getSizeMappings(type: string) {
    let mappingBuilder = googletag.sizeMapping();
    switch (type) {
      case 'billboard':
        mappingBuilder = mappingBuilder
          .addSize(
            [1024, 0],
            [
              [728, 90],
              [970, 31],
              [970, 250],
              [970, 90],
              [970, 550],
            ],
          ) //Billboard mapping
          .addSize([0, 0], []); //Show nothing mapping
        break;
      case 'slider':
        mappingBuilder = mappingBuilder
          .addSize([1024, 0], []) //Show nothing mapping
          .addSize([0, 0], [1, 1]); //Slider mapping
        break;
      case 'interstitial':
      case 'slider-all':
        //Slider empty mapping
        mappingBuilder = null;
        break;
      case 'mpu':
        mappingBuilder = mappingBuilder.addSize([0, 0], [300, 250]);
        break;
      case 'leaderboard-small':
        mappingBuilder = mappingBuilder.addSize([0, 0], [320, 50]); //Smartphone mapping
        break;
      case 'leaderboard-medium':
        mappingBuilder = mappingBuilder
          .addSize([1440, 0], [728, 90])
          .addSize([1024, 0], [320, 50])
          .addSize([0, 0], [320, 50]);
        break;
      case 'leaderboard':
      default:
        //Smartphone mapping
        mappingBuilder = mappingBuilder
          .addSize([1024, 0], [728, 90]) //Leaderboard mapping
          .addSize([0, 0], [320, 50]);
        break;
    }

    let mappings: googletag.SizeMapping[];
    if (mappingBuilder) {
      mappings = mappingBuilder.build();
    }

    return mappings;
  }

  async defineBanner(el: HTMLElement) {
    let opt: BannerOptions = null;
    const slotId = el.id;

    if (el.dataset.options) {
      try {
        opt = JSON.parse(el.dataset.options) as BannerOptions;
      } catch {
        opt = null;
      }
    }

    if (!opt) {
      el.classList.add('hidden');
      return;
    }
    el.classList.remove('hidden');

    opt = {
      width: 0,
      height: 0,
      position: '',
      type: '',
      ...opt,
    };

    const country = opt.countryTargeting ? await getCountry() : null;
    const compName = opt.competitionBanner ? opt.competitionBanner : null;
    const position = opt.position.includes('|') ? opt.position.split('|') : opt.position;
    const type = (opt.type ?? '').toLowerCase();
    const mappings = this.getSizeMappings(type);

    let slot = googletag
      .pubads()
      .getSlots()
      .find((s) => s.getSlotElementId() === slotId);
    if (slot) {
      googletag.pubads().refresh([slot], { changeCorrelator: false });
      return;
    }

    const outOfPage = outOfPageTypes.includes(type);
    if (outOfPage) {
      slot = googletag.defineOutOfPageSlot(opt.adPath, slotId);
    } else {
      slot = googletag.defineSlot(opt.adPath, [opt.width, opt.height], slotId);
    }

    if (!slot) {
      el.classList.add('hidden');
      return;
    }

    if (mappings) slot.defineSizeMapping(mappings);

    slot.addService(googletag.pubads()).setTargeting('pos', position);

    if (country) {
      slot
        .setTargeting('language', language.toUpperCase())
        .setTargeting('location', country.countryCode)
        .setTargeting('site', compName);
    }

    if (opt.hostCityTargeting) {
      slot.setTargeting('host_city', opt.hostCityTargeting.toLowerCase());
    }

    if (opt.sponsor) {
      slot.setTargeting('sponsor', opt.sponsor.replace("'", '')).setTargeting('competition', opt.competitionBanner);
    }

    const context = opt.context ?? bannerAdsContext;
    if (context) {
      slot.setTargeting('context', context);
    }

    googletag.display(slotId);

    outOfPage ? this.displayBanner(el) : this.observer.observe(el);
  }

  displayBanner(el: HTMLElement) {
    const slotId = el.id;
    const opt = JSON.parse(el.dataset.options) as BannerOptions;
    const type = (opt.type ?? '').toLowerCase();
    const slot = googletag
      .pubads()
      .getSlots()
      .find((s) => s.getSlotElementId() === slotId);

    googletag.cmd.push(() => {
      googletag.pubads().refresh([slot], { changeCorrelator: false });

      if (type === 'slider') this._sliderAdvHide(slotId);
    });
  }

  _sliderAdvHide(id: string) {
    if (!this.sliderTimer && window.scrollX === 0 && window.screenY >= 500) {
      this.sliderTimer = setTimeout(() => {
        const slider = document.querySelector(`#${id}.slider > div`);
        slider.parentElement.remove();
      }, 1000);
    }
  }

  static readonly instance = new GptBannerService();
}

const GptBannerServiceInstance = GptBannerService.instance;
GptBannerServiceInstance.init();

export default GptBannerServiceInstance;
